import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DomainSetting,
  ProviderSettingsOverrides,
} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {
  PaymentProvider,
  PaymentProviderSettings,
} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'provider-settings-overrides-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeSelectV3Component,
  ],
  templateUrl: './provider-settings-overrides-parameter.component.html',
})
export class ProviderSettingsOverridesParameterComponent implements OnInit {
  providerForm!: FormGroup;
  providerToAdd = new FormControl<PaymentProvider | null>(null);
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  draft: ProviderSettingsOverrides = {};
  activeProvider: PaymentProvider | null = null;
  addProviderItems: {value: PaymentProvider; label: string}[] = [];

  readonly allProviders = Object.values(PaymentProvider);

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    this.draft = {...this.parseValue()};
    this.providerForm = this.fb.group({
      transactionProxy: [false],
      onboarding: [false],
      requiresBankAccount: [false],
      creditCardAntifraud: [false],
    });
    this.refreshAddProviderItems();
  }

  private refreshAddProviderItems(): void {
    this.addProviderItems = this.allProviders
      .filter(p => this.draft[p] === undefined)
      .map(p => ({value: p, label: p}));
  }

  get configuredProviders(): PaymentProvider[] {
    return Object.keys(this.draft).filter(
      k => this.draft[k as PaymentProvider] !== undefined,
    ) as PaymentProvider[];
  }

  private parseValue(): ProviderSettingsOverrides {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const obj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as ProviderSettingsOverrides)
          : (raw as ProviderSettingsOverrides);
      return typeof obj === 'object' && obj !== null ? obj : {};
    } catch {
      return {};
    }
  }

  private patchFormFromSettings(cur?: Partial<PaymentProviderSettings>): void {
    this.providerForm.patchValue({
      transactionProxy: Boolean(cur?.transactionProxy),
      onboarding: Boolean(cur?.onboarding),
      requiresBankAccount: Boolean(cur?.requiresBankAccount),
      creditCardAntifraud: Boolean(cur?.creditCard?.antifraud),
    });
  }

  addProvider(): void {
    const p = this.providerToAdd.value;
    if (!p || this.draft[p] !== undefined) return;
    const empty = {
      transactionProxy: false,
      onboarding: false,
      requiresBankAccount: false,
      creditCard: {antifraud: false},
    };
    this.draft = {...this.draft, [p]: empty};
    this.activeProvider = p;
    this.patchFormFromSettings(empty);
    this.providerToAdd.setValue(null);
    this.refreshAddProviderItems();
  }

  editProvider(p: PaymentProvider): void {
    this.activeProvider = p;
    this.patchFormFromSettings(this.draft[p] ?? {});
  }

  removeProvider(p: PaymentProvider): void {
    const rest = {...this.draft};
    delete rest[p];
    this.draft = rest as ProviderSettingsOverrides;
    if (this.activeProvider === p) {
      this.activeProvider = null;
      this.providerForm.reset({
        transactionProxy: false,
        onboarding: false,
        requiresBankAccount: false,
        creditCardAntifraud: false,
      });
    }
    this.refreshAddProviderItems();
  }

  apply(): void {
    if (!this.activeProvider) return;
    const g = this.providerForm.value as {
      transactionProxy: boolean;
      onboarding: boolean;
      requiresBankAccount: boolean;
      creditCardAntifraud: boolean;
    };
    this.draft = {
      ...this.draft,
      [this.activeProvider]: {
        transactionProxy: Boolean(g.transactionProxy),
        onboarding: Boolean(g.onboarding),
        requiresBankAccount: Boolean(g.requiresBankAccount),
        creditCard: {antifraud: Boolean(g.creditCardAntifraud)},
      },
    };
  }

  onSend(): void {
    if (this.submitting) return;
    if (this.activeProvider) {
      this.apply();
    }
    this.submitting = true;
    const out: ProviderSettingsOverrides = {};
    for (const p of Object.keys(this.draft) as PaymentProvider[]) {
      const cur = this.draft[p];
      if (!cur) continue;
      out[p] = {
        transactionProxy: Boolean(cur.transactionProxy),
        onboarding: Boolean(cur.onboarding),
        requiresBankAccount: Boolean(cur.requiresBankAccount),
        creditCard: {antifraud: Boolean(cur.creditCard?.antifraud)},
      };
    }
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(out),
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
